import React, { useState } from "react";
import "shared/styles/table.scss";
import {
  Table,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from "reactstrap";
import t from "utils/Lang";
import moment from "moment";
import numeral from "numeral";
import { Icon } from "shared";
import ReactTooltip from "react-tooltip";

type ContractTableProps = {
  data: Array<any>;
  contractTypes: Array<any>;
  onEdit: (item: any) => void;
  onDelete: (item: any) => void;
  onDone: (item: any) => void;
  onMarkAsRentalFee: (item: any) => void;
};

type RowItemProps = {
  row: any;
  contractTypes: Array<any>;
  onEdit: (item: any) => void;
  onDelete: (item: any) => void;
  onDone: (item: any) => void;
  onMarkAsRentalFee: (item: any) => void;
};

const RowItem = ({
  row,
  contractTypes,
  onDelete,
  onDone,
  onEdit,
  onMarkAsRentalFee,
}: RowItemProps) => {
  const [isOpen, setOpen] = useState(false);
  const toggle = () => setOpen(!isOpen);
  const diffMonth = (contract: any) => {
    if (!contract.available_from || !contract.available_to) return "";
    return moment(contract.available_to).diff(moment(new Date()), "months");
  };
  return (
    <tr>
      <td>
        <div>{row.id}</div>
      </td>
      <td>
        <div>{row.name}</div>
      </td>
      <td>
        <div>
          {contractTypes.find((type: any) => type.id === row.contract_type_id)
            ?.name || ""}
        </div>
      </td>
      <td>
        <div>{row.available_from}</div>
      </td>
      <td>
        <div>{row.available_to}</div>
      </td>
      <td>
        <div>{diffMonth(row)}</div>
      </td>
      <td>
        <div>{numeral(row.deposit_fee).format("0,0")}</div>
      </td>
      <td>
        <div>{row.payment_period_monthly_cycle}</div>
      </td>
      <td>
        <div>
          {row?.payment_terms?.map?.((el: any) => (
            <div className="contact-item__row" key={el.id}>
              <span>
                {el.from_date} - {el.to_date}:{" "}
                {numeral(el.rental_fee_with_tax).format("0,0")}
              </span>
            </div>
          ))}
        </div>
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center" }}>
          {row.is_can_edit === false && (
            <div
              data-for={`contract-done-${row.id}`}
              data-tip={t().site_contract_modal__contract_done}
            >
              <Icon
                name="done"
                size="normal"
                style={{ backgroundColor: "var(--success)", color: "white" }}
              />
            </div>
          )}
          {row.is_can_edit === false && (
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`contract-done-${row.id}`}
            />
          )}
          {row.used_for_rental_fee_calculation && (
            <div
              className="money-icon__wrapper"
              data-for={`rental-fee-${row.id}`}
              data-tip={t().site_contract_modal__mark_as_rental_fee}
            >
              <i className="material-icons-outlined money-icon">attach_money</i>
            </div>
          )}
          {row.used_for_rental_fee_calculation && (
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`rental-fee-${row.id}`}
            />
          )}
        </div>
      </td>
      <td>
        <div>
          <Dropdown isOpen={isOpen} toggle={toggle}>
            <DropdownToggle className="more-button">
              <Icon name="more_horiz" size="medium" />
            </DropdownToggle>
            <DropdownMenu right>
              {(row.is_can_edit !== false && (
                <>
                  <DropdownItem onClick={() => onDone(row)}>
                    {t().site_contract_modal__done_contract}
                  </DropdownItem>
                  <DropdownItem onClick={() => onEdit(row)}>
                    {t().site_contract_modal__edit_contract}
                  </DropdownItem>
                  <DropdownItem onClick={() => onDelete(row)}>
                    {t().site_contract_modal__delete_contract}
                  </DropdownItem>
                </>
              )) || (
                <DropdownItem onClick={() => onEdit(row)}>
                  {t().site_contract_modal__view_contract}
                </DropdownItem>
              )}
              <DropdownItem onClick={() => onMarkAsRentalFee(row)}>
                {t().site_contract_modal__mark_as_rental_fee}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </td>
    </tr>
  );
};

const ContractTable = ({
  data,
  contractTypes,
  onDelete,
  onDone,
  onEdit,
  onMarkAsRentalFee,
}: ContractTableProps) => {
  const renderRow = (row: any, index: number) => {
    return (
      <RowItem
        row={row}
        contractTypes={contractTypes}
        key={row.id || index}
        onDelete={onDelete}
        onDone={onDone}
        onEdit={onEdit}
        onMarkAsRentalFee={onMarkAsRentalFee}
      />
    );
  };
  return (
    <div>
      <Table responsive striped>
        <thead>
          <tr>
            <th>
              <div className="table-header">
                <span>ID</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_contract_modal__name}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_contract_modal__type}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_contract_modal__date}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_contract_modal__expire}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_contract_modal__month_expire}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_listing__deposit}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().contract__payment_cycle}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_contract_modal__pricing}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>{t().site_listing__status}</span>
              </div>
            </th>
            <th>
              <div className="table-header">
                <span>Menu</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>{data?.map?.(renderRow)}</tbody>
      </Table>
    </div>
  );
};

export default ContractTable;
